import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/api/types/auth.types';

type PageState = 'loading' | 'success' | 'expired' | 'invalid';

export function ReverifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setState('invalid');
      return;
    }

    // Confirm-only (D-REVERIFY-LINK-CONFIRM-ONLY-1): the endpoint reactivates the account and
    // returns a confirmation — NO session/token. On success we land on a stable confirmation
    // panel with a Sign-in link; the user then completes the normal login flow (credentials + MFA).
    apiClient
      .post<ApiResponse<{ message: string }>>('/auth/reverify', { token })
      .then((res) => {
        if (!res.data.data) {
          const code = res.data.errors?.[0]?.code;
          setState(code === 'token_expired' ? 'expired' : 'invalid');
          return;
        }
        setState('success');
      })
      .catch((err) => {
        const code = err?.response?.data?.errors?.[0]?.code;
        setState(code === 'token_expired' ? 'expired' : 'invalid');
      });
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,159,253,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,159,253,1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-12 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Unified Cyber GRC Platform</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-8 shadow-2xl text-center">
          {state === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-accent-cyan" />
              <p className="text-sm text-text-secondary">Verifying your account…</p>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center gap-4 py-2">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <div>
                <h2 className="text-base font-semibold text-text-primary">Account re-verification completed</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Your account has been reactivated. Please sign in to continue.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Sign in
              </Button>
            </div>
          )}

          {state === 'expired' && (
            <div className="flex flex-col items-center gap-4 py-2">
              <XCircle className="h-8 w-8 text-amber-400" />
              <div>
                <h2 className="text-base font-semibold text-text-primary">Link expired</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  This reverification link has expired. Log in to request a new one.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Return to sign in
              </Button>
            </div>
          )}

          {state === 'invalid' && (
            <div className="flex flex-col items-center gap-4 py-2">
              <XCircle className="h-8 w-8 text-red-400" />
              <div>
                <h2 className="text-base font-semibold text-text-primary">Invalid link</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  This reverification link is invalid or has already been used.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Return to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
